/**
 * Shrinking a photo before it is uploaded.
 *
 * A phone camera produces 3-6MB per shot. Uploading that raw wastes the
 * engineer's data allowance in a van, wastes storage, and — for anything
 * that gets read by machine afterwards — wastes tokens on detail nobody
 * needs. So photos are resized and re-encoded in the browser first.
 *
 * There are TWO profiles, and the difference is the point of this file.
 *
 * EVIDENCE is a photo taken as a record: the room after a visit, a fault,
 * a bit of damage. A person looks at it to see what it shows. 1600px on
 * the long edge is plenty and has been the spec since the Glide app.
 *
 * DOCUMENT is a photo of a printed sheet whose SMALL PRINT has to be
 * read, by a person or a model — a garage health report, an invoice. At
 * 1600px the digits in a tyre table land around 20px tall, and this is
 * exactly the material where confusing "2mm" with "7mm" changes whether a
 * van is legal to drive. So a document keeps more resolution than an
 * evidence photo, deliberately, and pays for it in file size.
 *
 * Neither profile is a guess about what "looks fine": they are two
 * different jobs, and using the evidence numbers for a document is the
 * mistake this file exists to prevent.
 */

export interface CompressionProfile {
  /** Longest edge in pixels after resizing. */
  maxEdge: number;
  /** JPEG quality, 0..1. */
  quality: number;
}

/** A photo somebody will look at. Unchanged from the Service module's
 * original spec, carried over from the Glide app. */
export const EVIDENCE_PROFILE: CompressionProfile = { maxEdge: 1600, quality: 0.8 };

/**
 * A photo of a document whose small print has to be read.
 *
 * 2200px on the long edge puts an A4 sheet at roughly 190dpi, so 8-10pt
 * body text lands near 25-30px tall — comfortably legible rather than
 * marginal. Typically 400-700KB a page, which keeps a multi-page report
 * well inside the 5MB-per-image ceiling the reader enforces.
 */
export const DOCUMENT_PROFILE: CompressionProfile = { maxEdge: 2200, quality: 0.82 };

/** The web image formats a canvas re-encodes directly. A PDF is passed
 * through untouched — there is no canvas route to shrinking one, and they
 * are generally small already. HEIC is handled separately (see isHeic). */
const COMPRESSIBLE = ["image/jpeg", "image/png", "image/webp"];

export function isCompressibleImage(mimeType: string): boolean {
  return COMPRESSIBLE.includes(mimeType.toLowerCase());
}

/**
 * HEIC/HEIF — an iPhone's default camera format — has to be CONVERTED, not
 * just optionally shrunk.
 *
 * An <img> tag won't display image/heic on most browsers (Chrome, Firefox,
 * and even Safari unreliably), so a HEIC that reaches Storage unconverted
 * uploads fine and then renders as a broken / "unavailable" tile — exactly
 * what an engineer uploading a room photo from an iPhone hits. A WebKit
 * canvas (every iPhone) CAN decode HEIC, so compressImage() re-encodes it
 * to JPEG on the way up. iOS also sometimes hands the file over with an
 * empty MIME type, so sniff the extension when the type is missing. A
 * browser that genuinely can't decode HEIC falls back to the original,
 * exactly as before — no worse off than it already was.
 */
export function isHeic(file: { type: string; name: string }): boolean {
  const type = file.type.toLowerCase();
  if (type === "image/heic" || type === "image/heif") return true;
  return type === "" && /\.(heic|heif)$/i.test(file.name);
}

/**
 * The size to draw at: scaled down to fit maxEdge, never scaled UP.
 *
 * Enlarging a small photo adds bytes and no information, and on a
 * document it adds interpolation artefacts to the very characters
 * somebody is trying to read.
 */
export function targetSize(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= 0 || !Number.isFinite(longest)) return { width, height };
  const scale = Math.min(1, maxEdge / longest);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Whether the compressed version is actually worth using.
 *
 * Re-encoding is not always a win: a screenshot is already a small, crisp
 * PNG, and pushing it through JPEG can come out LARGER and blurrier at
 * once. When that happens the original is the better upload on both
 * counts, so it wins. This is why the caller must ask rather than assume
 * the output of a compressor is smaller than its input.
 */
export function compressionWorthIt(originalBytes: number, compressedBytes: number): boolean {
  if (!Number.isFinite(compressedBytes) || compressedBytes <= 0) return false;
  return compressedBytes < originalBytes;
}

/**
 * Resize and re-encode, in the browser.
 *
 * Returns the ORIGINAL file when it is not a compressible image, when the
 * canvas is unavailable, or when compressing made it bigger — so the
 * caller can always upload what comes back without checking which it got.
 * The mime type comes back too, because a PNG that survived is still a
 * PNG and must not be uploaded labelled as JPEG.
 */
export async function compressImage(
  file: File,
  profile: CompressionProfile = EVIDENCE_PROFILE,
): Promise<{ blob: Blob; mimeType: string }> {
  const heic = isHeic(file);
  if (!isCompressibleImage(file.type) && !heic) return { blob: file, mimeType: file.type };

  try {
    // imageOrientation "from-image" applies the EXIF rotation a phone writes,
    // so a shot taken in portrait doesn't land sideways once re-encoded. It
    // matters most for a HEIC straight off an iPhone, which always carries it.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const { width, height } = targetSize(bitmap.width, bitmap.height, profile.maxEdge);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { blob: file, mimeType: file.type };
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", profile.quality);
    });
    if (!blob) return { blob: file, mimeType: file.type };

    // A HEIC/HEIF original can't be shown in an <img>, so once we've decoded
    // it we keep the JPEG even when it came out larger — a visible photo
    // beats a smaller invisible one. For a format the browser can already
    // display, only swap when the re-encode is genuinely smaller.
    if (heic) return { blob, mimeType: "image/jpeg" };
    return compressionWorthIt(file.size, blob.size)
      ? { blob, mimeType: "image/jpeg" }
      : { blob: file, mimeType: file.type };
  } catch {
    // A decode failure is not a reason to lose the upload.
    return { blob: file, mimeType: file.type };
  }
}
